import type * as React from "react";
import { lazy, Suspense, useEffect, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router";
import {
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { AppShell } from "@/layout/AppShell";
import { LoginPage } from "@/pages/Login";
import { SetPasswordPage } from "@/pages/SetPassword";
// §v3.pw-reset — public (logged-out) password-reset pages.
import { ForgotPasswordPage } from "@/pages/ForgotPassword";
import { ResetPasswordPage } from "@/pages/ResetPassword";
// §v3.mfa — operator self-service 2FA enrollment (authed, in-shell).
import { SecurityPage } from "@/pages/Security";
import { SoftwareUpdatesPage } from "@/pages/SoftwareUpdates";
// §v3.settings-hub (2026-05-25) — Settings umbrella shell. Renders the
// glass tab strip (Workspace / Users / Roles & permissions / Security)
// and an <Outlet/> for the active surface. Eager (tiny) so the strip
// paints instantly; its lazy children load under its own Suspense.
import { SettingsLayout } from "@/pages/SettingsLayout";
// §v3.profile-picker (2026-05-15) — post-login workspace selector
// (one tile per workspace the operator actually belongs to). Eager
// import (small file, always reached after login) so the picker
// paints with no Suspense flash.
import { ProfilePickerPage } from "@/pages/ProfilePicker";
// §v3.code-split (2026-05-07) — every authenticated page is now
// lazy-loaded so the initial JS bundle drops to just the shell +
// login + router. Each page is fetched on first navigation,
// then cached by the browser. Combined with hover-prefetch on
// sidebar items, this means the page's JS chunk arrives BEFORE
// the user clicks too. Net: faster first paint, no perceived
// delay on click. Linear / Vercel / Stripe pattern.
const lazyPage = (
  importer: () => Promise<Record<string, unknown>>,
  exportName: string,
) =>
  lazy(() =>
    importer().then((m) => ({
      default: m[exportName] as React.ComponentType<unknown>,
    })),
  );

// §v3.analytics-retired (2026-05-12) — Analytics surface removed
// per founder ask. Reports covers the same ground (per-domain
// time series + CSV/PDF export) and Overview's new TodaysPulse
// + ActivityChart + sentiment block cover the daily-driver view.
// The Analytics.tsx file is left on disk so git history reads
// cleanly; delete on the next code-cleanup pass.
const ReportsPage = lazyPage(
  () => import("@/pages/Reports"),
  "ReportsPage",
);
const OnboardingWizardPage = lazyPage(
  () => import("@/pages/onboarding/OnboardingWizard"),
  "OnboardingWizardPage",
);
// §billing.trust-page — public Trust & Security page (sharable during security
// reviews; no auth, no AppShell chrome).
const TrustPage = lazyPage(() => import("@/pages/Trust"), "TrustPage");
// §usage-billing (2026-07-12) — tenant Usage & billing console (metered
// calls/chats, credit meter, top-ups, subscription lifecycle).
const UsageBillingPage = lazyPage(
  () => import("@/pages/UsageBilling"),
  "UsageBillingPage",
);
// §v3.agent-studio (2026-06-01) — unified Agent Studio consolidating
// Agent voice + AI Simulation + Training into one tabbed workspace. It
// dynamically imports the real AiSimulation + Training pages as tab
// bodies, so those modules no longer need a top-level lazy decl here.
const AgentStudioPage = lazyPage(
  () => import("@/pages/agent-studio/AgentStudio"),
  "AgentStudioPage",
);
// §v3.brand (2026-05-11) — operator-editable brand facts page.
// The voice agent reads from the same Postgres rows this page
// writes to, so edits propagate live (cache-invalidate on save).
// §v3.kb-merge (2026-05-12) — Brand page is now mounted from
// inside KnowledgePage's "Canonical answers" tab; no longer
// imported at the top-level router (the legacy /brand route
// redirects to /knowledge). KnowledgePage is the unified
// surface wrapping Brand + Products/Q&A under one route.
const KnowledgePage = lazyPage(
  () => import("@/pages/Knowledge"),
  "KnowledgePage",
);
const ConversationDetailPage = lazyPage(
  () => import("@/pages/ConversationDetail"),
  "ConversationDetailPage",
);
// §v3.drop-conversations (2026-05-08) — InboxPage list view
// retired; the /inbox list route now redirects to /enquiries and
// the /inbox/:id detail route uses ConversationDetailPage directly.
// The pages/Inbox.tsx file itself is left on disk but unimported
// so it's a no-op at the bundle level — keeps git history readable
// without inviting accidental re-mount. Delete the file the next
// time you do a code-cleanup pass.
const LiveCallsPage = lazyPage(
  () => import("@/pages/LiveCalls"),
  "LiveCallsPage",
);
const EnquiriesPage = lazyPage(
  () => import("@/pages/Enquiries"),
  "EnquiriesPage",
);
const ProductsPage = lazyPage(
  () => import("@/pages/Products"),
  "ProductsPage",
);
// §settings-redesign — focused Settings pages (single left-sidebar IA). Each is
// a named export from one module, so they share a chunk.
const ChannelsSettings = lazyPage(() => import("@/pages/settings/sections"), "ChannelsSettings");
const PhoneSettings = lazyPage(() => import("@/pages/settings/sections"), "PhoneSettings");
const NotificationsSettings = lazyPage(() => import("@/pages/settings/sections"), "NotificationsSettings");
const MarketingSettings = lazyPage(() => import("@/pages/settings/sections"), "MarketingSettings");
const PaymentsSettings = lazyPage(() => import("@/pages/settings/sections"), "PaymentsSettings");
const SecuritySettings = lazyPage(() => import("@/pages/settings/sections"), "SecuritySettings");
const ProvidersSettings = lazyPage(() => import("@/pages/settings/sections"), "ProvidersSettings");
const BillingSettings = lazyPage(() => import("@/pages/settings/sections"), "BillingSettings");
const EmailSendingSettings = lazyPage(() => import("@/pages/settings/sections"), "EmailSendingSettings");
const BusinessProfileSettings = lazyPage(() => import("@/pages/settings/sections"), "BusinessProfileSettings");
const RolesPermissionsPage = lazyPage(
  () => import("@/pages/RolesPermissions"),
  "RolesPermissionsPage",
);
const UsersPage = lazyPage(() => import("@/pages/Users"), "UsersPage");
const ApprovalsPage = lazyPage(() => import("@/pages/Approvals"), "ApprovalsPage");
// §channels-page — the self-serve storefront of places the agent answers.
const ChannelsPage = lazyPage(() => import("@/pages/Channels"), "ChannelsPage");
const AuditLogPage = lazyPage(() => import("@/pages/AuditLog"), "AuditLogPage");
const AgentQAPage = lazyPage(() => import("@/pages/AgentQA"), "AgentQAPage");
// §v3.marketing (2026-05-25) — outbound Marketing surface (Phase 1: functional
// Audience builder on real customers; Campaigns/Automations/Performance staged).
const MarketingPage = lazyPage(() => import("@/pages/Marketing"), "MarketingPage");
// §xpand-ai (2026-06-09) — internal "ask anything about your business" copilot.
const XpandAiPage = lazyPage(() => import("@/pages/XpandAi"), "XpandAiPage");
// §mission-control — the founder's fleet operator console (own full-screen
// glass shell outside AppShell). Master-gated inside the page.
const MissionControlPage = lazyPage(
  () => import("@/pages/mission-control/MissionControl"),
  "MissionControlPage",
);
// §onboarding-wizard-6 — faithful 6-step design port of the self-serve
// signup wizard (public, no AppShell). Supersedes the earlier 5-step
// GetStarted page at the same /get-started route (that superseded file has
// since been deleted — it carried fabricated business facts). Mounts the new
// OnboardingWizard which builds a deployment manifest and submits it via the
// real provisioning client (submitProvisioning) + polls getProvisioningRequest.
const GetStartedPage = lazyPage(
  () => import("@/pages/onboarding-next/OnboardingWizard"),
  "OnboardingWizardPage",
);
// §v3.propose-edits (2026-05-13) — operator review queue for the
// structured edits Grok proposes from training sessions. Lives under
// /training so the perms gate matches.
const ProposedEditsPage = lazyPage(
  () => import("@/pages/ProposedEdits"),
  "ProposedEditsPage",
);
const CustomerProfilePage = lazyPage(
  () => import("@/pages/CustomerProfile"),
  "CustomerProfilePage",
);
const OverviewPage = lazyPage(
  () => import("@/pages/Overview"),
  "OverviewPage",
);
const OrdersPage = lazyPage(() => import("@/pages/Orders"), "OrdersPage");
const OrderDetailPage = lazyPage(
  () => import("@/pages/OrderDetail"),
  "OrderDetailPage",
);
const SupportTicketsPage = lazyPage(
  () => import("@/pages/SupportTickets"),
  "SupportTicketsPage",
);
const SupportTicketDetailPage = lazyPage(
  () => import("@/pages/SupportTicketDetail"),
  "SupportTicketDetailPage",
);
// Public surfaces — also lazy so the auth-less landing experience
// is even cheaper.
const CallRosiePage = lazyPage(
  () => import("@/pages/CallRosie"),
  "CallRosiePage",
);
const AkillisSitePage = lazyPage(
  () => import("@/pages/AkillisSite"),
  "AkillisSitePage",
);
import { isAuthenticated, SESSION_CLEARED_EVENT } from "@/lib/auth";
import { getTenantId } from "@/lib/auth";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { RequirePermission, SessionGate, usePermissions } from "@/lib/permissions";
import type { Permission } from "@/lib/rbac";
import { NoAccessPage } from "@/pages/NoAccess";

// §v3.realtime-dashboard (2026-05-05) — every list / detail in the
// dashboard auto-refreshes every 5 seconds. The agent writes to the
// DB on tool-call (place_order, create_support_ticket) and on every
// inbound conversation turn; without this the UI lagged behind by
// however long since the operator's last manual reload. React
// Query dedupes concurrent fetches to the same key + skips re-renders
// when the data shape hasn't changed, so the wire cost is one cheap
// JSON request per visible list per 5s — well under the v3 backend's
// budget for a single-tenant deployment.
//
// Per-call sites can override (longer interval for low-volatility
// queries like products / Q&A / settings — see the call site to
// pass ``refetchInterval: 30_000`` when 5s would be wasted churn).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // §v3.staleness-perf (2026-05-07) — was ``staleTime: 0``,
      // which forced a refetch on every tab switch even if the
      // cache was milliseconds old. Linear/Vercel/Stripe pattern:
      // bump staleTime so cached data is considered fresh for
      // 30s. Tab switching with a warm cache becomes instant
      // (no network call). After 30s, refetchOnMount fires; the
      // background polling at refetchInterval also catches
      // changes between explicit interactions. The end-to-end
      // feel is "cached data renders immediately, fresh data
      // streams in within 30s" — stale-while-revalidate done
      // right.
      staleTime: 30_000,
      // §v3.poll-perf (2026-05-07) — was 5_000 ms which fired a
      // refetch of every active query every 5 seconds. With slow
      // endpoints (customers list rolled up at 4–5s before the
      // ticket-N+1 fix) that produced perpetual background
      // network/CPU load and made tab switches feel sluggish.
      // 30s is still well within "feels live" for a single-
      // operator dashboard; ``refetchOnWindowFocus`` below
      // catches the moment the operator returns to the tab.
      refetchInterval: 30_000,
      refetchIntervalInBackground: false,
      // Refetch when the operator switches back to the tab —
      // ensures they see the latest before they look at any
      // stale cached value.
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: 1,
    },
  },
});

function RequireAuth({ children }: { children: React.JSX.Element }) {
  const location = useLocation();
  const [authed, setAuthed] = useState<boolean>(isAuthenticated);

  // Re-check on storage events so a cross-tab sign-out lands here too.
  // §stale-session (2026-08-10) — ALSO listen for the same-tab signal:
  // "storage" only fires in other tabs, so when api.ts cleared a dead
  // session (401 + failed refresh) THIS tab kept its shell mounted until
  // the operator manually navigated. clearSession now dispatches
  // SESSION_CLEARED_EVENT; a dead session bounces straight to /login.
  useEffect(() => {
    const onStorage = () => setAuthed(isAuthenticated());
    window.addEventListener("storage", onStorage);
    window.addEventListener(SESSION_CLEARED_EVENT, onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(SESSION_CLEARED_EVENT, onStorage);
    };
  }, []);

  if (!authed) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
}

// §rbac (2026-05-08) — short alias for ``RequirePermission`` to keep
// route declarations one line each. ``p`` is short for permission.
function Guard({ p, children }: { p: Permission; children: React.ReactNode }) {
  return <RequirePermission permission={p}>{children}</RequirePermission>;
}

// §hotel-removal (2026-08-19) — the demo tenant's console + mock-data
// surfaces are gone, and with them the four `tenant === "hotel"` route
// dispatchers (KnowledgeRouter / OverviewRouter / RequireHotel /
// HomeRedirect). Every tenant now lands on /overview and renders the
// shared pages; per-industry differences come from the DashboardConfig
// registry (lib/dashboardConfig.ts), which keys on the tenant's resolved
// industry PACK, never on a tenant id.

// §v3.settings-hub (2026-05-25) — index for /settings. Workspace owns
// the bare /settings path, but it's gated on settings.view; an operator
// without it (e.g. someone who only manages Users, or only their own
// Security) must still land somewhere useful instead of being bounced
// out of Settings entirely. So we render Workspace when allowed, else
// forward to the first tab the operator CAN see. Security is universal,
// so this never dead-ends. We wait on the perms load to avoid briefly
// redirecting a master admin (whose perms arrive a beat after mount).
function SettingsHome() {
  const { has, loading } = usePermissions();
  if (loading) return <div aria-busy="true" style={{ minHeight: "40vh" }} />;
  if (has("settings.view")) return <Navigate to="/settings/channels" replace />;
  if (has("operators.view")) return <Navigate to="/settings/members" replace />;
  if (has("roles.view")) return <Navigate to="/settings/roles" replace />;
  return <Navigate to="/settings/security" replace />;
}

function AppRoutes() {
  // react-query client is provided at the app root so every page
  // can useQuery against the admin API without threading a prop.
  useQueryClient();
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* §onboarding-activation — first-login "set your password" (authed via
          the magic-login session that just landed). */}
      <Route path="/set-password" element={<SetPasswordPage />} />
      {/* §v3.pw-reset — public password-reset pages (no auth). */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* §v3.profile-picker — post-login workspace selector.
          Reached after a successful login + via "Switch profile"
          from inside the AppShell. No Guard wrapper: an authed
          operator can always view the picker. */}
      <Route path="/select-profile" element={<ProfilePickerPage />} />
      {/* §rbac — terminal page for operators whose role grants no
          view permissions on any sidebar destination. Reached via
          ``firstAccessibleRoute`` returning NO_ACCESS_ROUTE from the
          login flow or from a Guard's redirect. Lives outside the
          AppShell because the shell would render an empty sidebar
          for someone with no access. */}
      <Route path="/no-access" element={<NoAccessPage />} />
      {/* §4.3: /onboarding is the cold-signup wizard. Full-screen
          (own chrome, own keyboard handling) — no AppShell sidebar.
          Anonymous visitors use it without a JWT; authed operators
          clicking the sidebar's Onboarding link land here too. */}
      <Route path="/onboarding" element={<OnboardingWizardPage />} />
      {/* §v3.public-call — public "Call Rosie" landing page. No
          auth, no AppShell chrome. Browser-direct WebRTC to OpenAI
          Realtime via an ephemeral token minted server-side by
          /api/public/voice/session. This is what we point the
          public domain at (e.g. https://rozeskin.ai/call). */}
      <Route path="/call" element={<CallRosiePage />} />
      {/* §v3.multi-tenant — per-tenant public widget URL. The page reads
          the tenant off the path and mints via
          /api/public/voice/session/<tenant> (opt-in enforced server-side).
          Point a 2nd tenant's public domain at /call/<tenant>. */}
      <Route path="/call/:tenant" element={<CallRosiePage />} />
      {/* §billing.trust-page — public Trust & Security page. Share with a
          prospect's security/procurement reviewer; closes most mid-market
          security asks without a SOC 2 audit. */}
      <Route path="/trust" element={<TrustPage />} />
      {/* §v3.akillis-site — marketing site for AKILLIS TECHNOLOGY.
          Single-page marketing surface, no auth, no AppShell
          chrome. Lives at /akillis in dev; when the akillis.tech
          domain is mapped, point the domain root at this page. */}
      <Route path="/akillis" element={<AkillisSitePage />} />
      {/* §mission-control — founder fleet console; own glass shell, master-gated
          inside the page (RequirePermission would render inside AppShell).
          §stale-session (2026-08-10) — the standalone shells get the same
          SessionGate as AppShell: token exists ≠ token works, and a dead
          session must land on /login, not a dead console frame. */}
      <Route
        path="/mission-control"
        element={<RequireAuth><SessionGate><Suspense fallback={null}><MissionControlPage /></Suspense></SessionGate></RequireAuth>}
      />
      <Route
        path="/mission-control/tenant/:tenantId"
        element={<RequireAuth><SessionGate><Suspense fallback={null}><MissionControlPage /></Suspense></SessionGate></RequireAuth>}
      />
      {/* §onboarding-redesign — public self-serve onboarding (own glass shell,
          no auth, no AppShell). Submits a manifest to the provisioning queue. */}
      <Route
        path="/get-started"
        element={<Suspense fallback={null}><GetStartedPage /></Suspense>}
      />
      {/* short alias — printed on decks/QRs; must survive as a URL */}
      <Route path="/start" element={<Navigate to="/get-started" replace />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            {/* §stale-session (2026-08-10) — SessionGate holds a neutral
                splash until /me settles, so a dead session goes STRAIGHT
                to /login and the empty-sidebar shell frame is never
                committed (the "flash then /no-access" boot bug). */}
            <SessionGate>
              <AppShell />
            </SessionGate>
          </RequireAuth>
        }
      >
        {/* §v3.dashboard-overview — index now lands on Overview, the
            new daily-driver dashboard with KPI strip + activity
            previews. Operators who bookmark "/" land on the
            summary page; clicking into a section pulls them into
            inbox/orders/support. */}
        <Route index element={<Navigate to="/overview" replace />} />
        <Route path="overview" element={<Guard p="overview.view"><OverviewPage /></Guard>} />
        {/* §v3.settings-hub (2026-05-25) — /security moved UNDER Settings
            as a tab. The old top-level path redirects into the Settings
            shell so deep links + the sidebar's historical entry survive. */}
        <Route path="security" element={<Navigate to="/settings/security" replace />} />
        {/* §v3.drop-conversations (2026-05-08) — the /inbox list
            page is gone (operator ask: redundant with Live calls /
            Enquiries / Orders / Support). The /inbox/:id detail
            route stays — Order detail, Support detail, Timeline,
            and Overview all deep-link to it. A bare /inbox now
            redirects forward to /enquiries since that's the
            closest replacement for the catch-all conversations
            list. Permission-gated to keep RBAC parity with
            francis's main: the redirect target itself enforces
            its own ``enquiries.view`` permission below. */}
        <Route path="inbox" element={<Navigate to="/enquiries" replace />} />
        <Route path="inbox/:conversationId" element={<Guard p="conversations.view"><ConversationDetailPage /></Guard>} />
        <Route path="live-calls" element={<Guard p="live_calls.view"><LiveCallsPage /></Guard>} />
        {/* §v3.enquiries (2026-05-08) — conversations where the
            customer engaged but didn't place an order or open a
            ticket. The "asked, didn't buy" cohort. RBAC: shares
            the conversations permission since enquiries is a
            view over the same conversation table. */}
        <Route path="enquiries" element={<Guard p="conversations.view"><EnquiriesPage /></Guard>} />
        <Route path="orders" element={<Guard p="orders.view"><OrdersPage /></Guard>} />
        <Route path="orders/:orderId" element={<Guard p="orders.view"><OrderDetailPage /></Guard>} />
        <Route path="support" element={<Guard p="support.view"><SupportTicketsPage /></Guard>} />
        <Route path="support/:ticketId" element={<Guard p="support.view"><SupportTicketDetailPage /></Guard>} />
        <Route path="customers" element={<Guard p="customers.view"><CustomerProfilePage /></Guard>} />
        {/* §v3.marketing (2026-05-25) — gated on customers.view (the Audience
            builder reads customer data); a dedicated marketing.* permission can
            be added later. */}
        <Route path="marketing" element={<Guard p="customers.view"><MarketingPage /></Guard>} />
        {/* §xpand-ai (2026-06-09) — internal copilot. Gated on overview.view
            (baseline dashboard access) to match the backend endpoint gate. */}
        <Route path="ask" element={<Guard p="overview.view"><XpandAiPage /></Guard>} />
        <Route path="products" element={<Guard p="products.view"><ProductsPage /></Guard>} />
        {/* §usage-billing — consumption + credits + subscription. Gated on
            settings.view to match the /api/billing/* endpoints. */}
        <Route path="usage" element={<Guard p="settings.view"><UsageBillingPage /></Guard>} />
        <Route path="approvals" element={<Guard p="orders.view"><ApprovalsPage /></Guard>} />
        <Route path="audit-log" element={<Guard p="audit_log.view"><AuditLogPage /></Guard>} />
        <Route path="agent-qa" element={<Guard p="settings.view"><AgentQAPage /></Guard>} />
        {/* §v3.stock-forecast-tab (2026-05-25) — the AI Stock Forecast is a
            tab on the Products page (same component, forecast tab active). */}
        <Route path="products/forecast" element={<Guard p="products.view"><ProductsPage /></Guard>} />
        {/* §v3.kb-merge (2026-05-12) — /products/qa redirects into
            the unified Knowledge page's Q&A tab. Both routes
            preserved so bookmarks and the sidebar shortcut keep
            working without UI changes for the operator. */}
        <Route path="products/qa" element={<Navigate to="/knowledge/qa" replace />} />
        {/* §v3.agent-studio (2026-06-01) — unified Agent Studio. The old
            /agent, /training, /ai-simulation entries are consolidated into
            tabs here; their paths redirect below so bookmarks survive. */}
        <Route path="channels" element={<Guard p="settings.view"><ChannelsPage /></Guard>} />
        <Route path="agent-studio" element={<Guard p="agent.view"><AgentStudioPage /></Guard>} />
        <Route path="agent-studio/:tab" element={<Guard p="agent.view"><AgentStudioPage /></Guard>} />
        {/* Legacy → Agent Studio tab redirects. */}
        <Route path="agent" element={<Navigate to="/agent-studio/persona" replace />} />
        {/* §v3.kb-merge (2026-05-12) — unified Knowledge page.
            Two tabs:
              /knowledge       → Canonical answers (wraps BrandPage)
              /knowledge/qa    → Custom Q&A (wraps ProductsPage qa tab)
            ``settings.view`` permission scopes the canonical tab
            (editing a brand fact = editing the prompt body); the
            Q&A tab inherits because both edit voice/chat answers. */}
        <Route path="knowledge" element={<Guard p="settings.view"><KnowledgePage /></Guard>} />
        <Route path="knowledge/qa" element={<Guard p="settings.view"><KnowledgePage /></Guard>} />
        {/* §v3.kb-docs (2026-06-09) — free-text Knowledge Documents tab (the
            tier-0 RAG source that replaces the baked-in About_RozeSkincare.md). */}
        <Route path="knowledge/docs" element={<Guard p="settings.view"><KnowledgePage /></Guard>} />
        <Route path="knowledge/review" element={<Guard p="settings.view"><KnowledgePage /></Guard>} />
        {/* Legacy /brand path — redirect into Knowledge's canonical
            tab so existing bookmarks keep working. */}
        <Route path="brand" element={<Navigate to="/knowledge" replace />} />
        {/* §v3.train-retired (2026-06-01) — the Train tab was removed; Teach
            mode (Agent Studio › Teach) replaces it. Legacy /training lands there. */}
        <Route path="training" element={<Navigate to="/agent-studio/teach" replace />} />
        <Route path="training/proposed-edits" element={<Guard p="training.view"><ProposedEditsPage /></Guard>} />
        {/* §v3.simulation-merge-v3 (2026-05-12) — AI Simulation
            is one merged surface with an internal Chat/Voice
            tab control. Mounts the real production agents
            (sendTestChatMessage + Grok Realtime via
            GrokLiveSession). Legacy /test-chat + /test-voice
            deep links redirect into the matching tab so existing
            bookmarks survive. */}
        {/* §v3.agent-studio — AI Simulation is now the Simulate tab. The
            standalone routes redirect into it (the Simulate tab mounts the
            same AiSimulationPage with its internal Chat/Voice control). */}
        <Route path="ai-simulation" element={<Navigate to="/agent-studio/simulate" replace />} />
        <Route path="ai-simulation/chat" element={<Navigate to="/agent-studio/simulate" replace />} />
        <Route path="ai-simulation/voice" element={<Navigate to="/agent-studio/simulate" replace />} />
        <Route path="test-chat" element={<Navigate to="/ai-simulation/chat" replace />} />
        <Route path="test-voice" element={<Navigate to="/ai-simulation/voice" replace />} />
        {/* §v3.analytics-retired (2026-05-12) — `/analytics` route
            removed. Old bookmarks redirect to /reports (the
            operational deep-dive that covers the same metrics with
            custom date range + export). */}
        <Route path="analytics" element={<Navigate to="/reports" replace />} />
        {/* §v3.reports — operational deep-dive: per-domain (Orders /
            Calls / Chat / Overview), custom date range, CSV+PDF export. */}
        <Route path="reports" element={<Guard p="reports.view"><ReportsPage /></Guard>} />
        {/* §v3.settings-hub (2026-05-25) — Settings is now the umbrella
            for Workspace config, Users, Roles & permissions, and the
            operator's own Security. SettingsLayout renders the glass tab
            strip + <Outlet/>; each tab keeps its own route + page + the
            same permission Guard it had as a top-level page. The bare
            /settings index renders Workspace (SettingsHome forwards an
            operator without settings.view to their first allowed tab).
            Old top-level /users, /roles, /security paths redirect into the
            matching tab so bookmarks + firstAccessibleRoute keep working. */}
        <Route path="settings" element={<SettingsLayout />}>
          <Route index element={<SettingsHome />} />
          {/* §settings-redesign — Workspace split into focused pages. */}
          {/* §brand-editable (2026-08-10) — the panel was built and wired to a
              working endpoint, then left unrouted; a tenant Owner could not
              rename their own business from anywhere in the product. */}
          <Route path="business" element={<Guard p="settings.view"><BusinessProfileSettings /></Guard>} />
          <Route path="channels" element={<Guard p="settings.view"><ChannelsSettings /></Guard>} />
          <Route path="phone" element={<Guard p="settings.view"><PhoneSettings /></Guard>} />
          <Route path="notifications" element={<Guard p="settings.view"><NotificationsSettings /></Guard>} />
          {/* §settings-ia-v2 (2026-07-11) — Email sending was orphaned by the
              2026-06 redesign (panel fully wired, no route). Restored. */}
          <Route path="email" element={<Guard p="settings.view"><EmailSendingSettings /></Guard>} />
          <Route path="marketing" element={<Guard p="settings.view"><MarketingSettings /></Guard>} />
          <Route path="payments" element={<Guard p="settings.view"><PaymentsSettings /></Guard>} />
          <Route path="providers" element={<Guard p="settings.view"><ProvidersSettings /></Guard>} />
          <Route path="security" element={<Guard p="settings.view"><SecuritySettings /></Guard>} />
          <Route path="billing" element={<Guard p="settings.view"><BillingSettings /></Guard>} />
          {/* "Users" → "Members"; old path redirects. */}
          <Route path="members" element={<Guard p="operators.view"><UsersPage /></Guard>} />
          <Route path="users" element={<Navigate to="/settings/members" replace />} />
          <Route path="roles" element={<Guard p="roles.view"><RolesPermissionsPage /></Guard>} />
          {/* §settings-ia-v2 — was path="security", a DUPLICATE of the
              workspace-guardrails route above; React Router matched the
              first, so this page (2FA, password, sign-in activity) was
              UNREACHABLE for everyone. Own path now. No perm gate — every
              operator manages their own sign-in security. */}
          <Route path="account-security" element={<SecurityPage />} />
          {/* §software-updates — universal "What's New" / version surface. */}
          <Route path="whats-new" element={<SoftwareUpdatesPage />} />
        </Route>
        <Route path="users" element={<Navigate to="/settings/members" replace />} />
        {/* §v3.settings-hub (2026-05-25) — /roles moved UNDER Settings;
            old path redirects into the Roles & permissions tab. */}
        <Route path="roles" element={<Navigate to="/settings/roles" replace />} />
        {/* §mc-tenant-admin (2026-06-04) — the standalone platform-admin
            tenant directory (/master/tenants, MasterPanel) was folded into
            Mission Control's Fleet directory + tenant detail. Old bookmarks
            redirect there so nothing 404s. */}
        <Route path="master/tenants" element={<Navigate to="/mission-control" replace />} />
        {/* §v3.settings-business (2026-04-27) — the day-2 business-
            details editor moved into Settings. This redirect catches
            stale bookmarks and the wizard's existing post-provisioning
            redirect (see OnboardingWizard.tsx) without breaking links.
            §v3.settings-hub (2026-05-25) — the old `#business` section
            anchor was removed in the settings-trim pass, so this now
            points at the Settings Workspace tab. */}
        <Route
          path="onboarding/edit"
          element={<Navigate to="/settings" replace />}
        />
      </Route>
      {/* §v3.drop-conversations — catch-all now lands on /overview
          since /inbox no longer renders a list. */}
      <Route path="*" element={<Navigate to="/overview" replace />} />
    </Routes>
  );
}

// §v3.persist-cache (2026-05-07) — persist the react-query cache
// to localStorage so cache survives a full reload / browser
// restart. After a refresh, list pages render their last-known
// data instantly while a background refetch arrives within a
// second or two. Linear/Vercel pattern. ``maxAge`` caps how
// stale a persisted entry can be before being dropped on
// hydration. The persister silently no-ops when localStorage
// is unavailable (private browsing, etc.).
const persister =
  typeof window !== "undefined" && typeof window.localStorage !== "undefined"
    ? createSyncStoragePersister({
        storage: window.localStorage,
        key: "expannd.rq.cache.v1",
        // Best-effort: skip persistence if a single entry is
        // bigger than 2 MB (keeps localStorage from filling up
        // with audio blobs or huge transcripts).
        throttleTime: 1000,
      })
    : null;

export function App() {
  const tree = (
    // §react-router-8 (2026-08-06) — the `future` prop is gone. It opted into
    // v7_startTransition + v7_relativeSplatPath, and both are simply the
    // behaviour now, so v8 removed the escape hatch. Having set those flags
    // early is exactly why this migration was one deleted prop rather than a
    // rewrite: the app has been running v7 semantics for months.
    <BrowserRouter>
      {/* §v3.no-flash-suspense (2026-05-11) — the inner Suspense
          boundary lives inside AppShell (wraps the Outlet only).
          THIS outer boundary stays as a safety net for the public
          / pre-auth routes (Login, Onboarding, Call Rosie,
          Akillis) that don't go through AppShell — and its
          fallback is now the loaded splash rather than `null`,
          so even those routes don't flash blank during chunk load.

          The previous design had ONE Suspense here with
          ``fallback={null}``, which unmounted the entire AppShell
          (sidebar + header) every time an authenticated route
          chunk loaded. The sidebar disappearing was the source of
          the "1-2 second blank flash" Akil reported. */}
      <ErrorBoundary>
        <Suspense
          fallback={
            <div
              aria-busy="true"
              style={{
                position: "fixed",
                inset: 0,
                background: "var(--bg-1, #0a0a0c)",
                pointerEvents: "none",
              }}
            />
          }
        >
          <AppRoutes />
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
  if (persister) {
    return (
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          // Drop persisted entries older than 24h so we don't
          // restore a stale snapshot from a week-old session.
          maxAge: 24 * 60 * 60 * 1000,
          // §v4.tenant-isolation (2026-08-13) — the cache is keyed by
          // query key alone, and query keys carry no tenant, so a
          // snapshot written under one workspace would rehydrate under
          // the next one and paint ITS customer rows. Busting on the
          // tenant id discards any persisted cache that belongs to a
          // different workspace, on boot, before a single row renders.
          buster: getTenantId(),
          // Skip persisting mutations and any query that errored.
          dehydrateOptions: {
            shouldDehydrateQuery: (q) =>
              q.state.status === "success" && !q.state.fetchFailureCount,
          },
        }}
      >
        {tree}
      </PersistQueryClientProvider>
    );
  }
  return (
    <QueryClientProvider client={queryClient}>{tree}</QueryClientProvider>
  );
}
